import React from 'react';
import moment from 'moment';
import ContactPageOrganizersForAddress, {
  GrubhubButton,
  grubhubCanOrderNow,
  grubhubOrderDaysBefore,
  isGiftCardServiceModule,
  serviceModules,
} from '@gik/calendar';
import bemBlock from '@gik/core/utils/bemBlock';
import { translationKeys } from './i18n/en';
import { useTranslation } from 'react-i18next';
import i18n from '@gik/i18n';
import DateTimeUtils, { isInPastAllTimezones } from '@gik/core/utils/DateTimeUtils';
import { getEndDateTime, getStartDateTime, IUserProfileClaim } from '@gik/calendar/models/Claim';
import { CalendarEventTypeIds } from '@gik/calendar/models/CalendarEventTypes';
import { CopyAddressButton, LinkParser, UI } from '@gik/ui';
import GiftCardClaimSection from '../GiftCardClaimSection/GiftCardClaimSection';
import { useInkind } from '@gik/api/inkinds/inkind';
import isInkindPageRecipientInfoComplete from '@gik/inkind-page/utils/isInkindPageRecipientInfoComplete';
import { AddToCalendarButton } from '@gik/calendar/components/AddToCalendarSelector/AddToCalendarButton';

export interface ClaimedItemModalProps {
  claimedItem: IUserProfileClaim;
  getFormattedTime: () => JSX.Element;
  dateFormat: string;
}

const contentBlockName = 'claimed-item-modal-content';

function ClaimedItemModal({ claimedItem, getFormattedTime, dateFormat }: ClaimedItemModalProps): JSX.Element {
  const bem = bemBlock(contentBlockName);
  const { t } = useTranslation();

  const { data: inkindPage } = useInkind(claimedItem.inkindRouteId);

  const isPastClaim = isInPastAllTimezones(getStartDateTime(claimedItem));

  // TODO: manage grubhub claims in a separate component, and if possible reuse code from the claim modal success component

  const eventCanOrderFromDate = getStartDateTime(claimedItem).startOf('day').subtract(grubhubOrderDaysBefore, 'days');
  const isGrubhub = claimedItem.serviceModule === serviceModules.Grubhub;
  const canOrderNow = isGrubhub && grubhubCanOrderNow(getStartDateTime(claimedItem));
  const isGiftCard = isGiftCardServiceModule(claimedItem.serviceModule);
  const isProvideMyself = claimedItem.serviceModule === serviceModules.ProvideMyself;

  return (
    <div className={bem('pages')}>
      <section className={bem('who')}>
        <div className={bem('section-title')}>{t(translationKeys.who)}</div>

        <a href={claimedItem.pageUrl} className={bem('who-container')}>
          <div
            className={bem('page-image')}
            style={claimedItem.pageThumbnail ? { backgroundImage: `url(${claimedItem.pageThumbnail})` } : {}}
          />
          <span className={bem('page-title')}>{claimedItem.pageTitle}</span>

          <div className={bem('who-more-info-icon')}>
            <i className="gik-icon-v2 icon-v2-chevronRight" />
          </div>
        </a>

        {!isGiftCard && !isPastClaim && (
          <div className={bem('address-container')}>
            <div className={bem('who-name')}>{claimedItem.recipientFullName}</div>
            {inkindPage && !isInkindPageRecipientInfoComplete(inkindPage) ? (
              <ContactPageOrganizersForAddress inkindRouteId={claimedItem.inkindRouteId} variant="centered" />
            ) : (
              <>
                <div className={bem('who-address')}>{claimedItem.recipientAddress}</div>
                <CopyAddressButton textToCopy={claimedItem.recipientAddress} />
              </>
            )}
          </div>
        )}

        {isGiftCard && <GiftCardClaimSection claimedItem={claimedItem} />}

        {isPastClaim && (
          <div className={bem('address-container')}>
            <div className={bem('address-not-displayed')}>{t(translationKeys.addressNotDisplayed)}</div>
          </div>
        )}
      </section>
      {!isGiftCard && (
        <section className={bem('when')}>
          <div className={bem('section-title')}>{t(translationKeys.when)}</div>

          <div className={bem('date-time-container')}>
            <div className={bem('date')}>{getStartDateTime(claimedItem).format(dateFormat)}</div>
            <div className={bem('time')}>{getFormattedTime()}</div>
          </div>

          {!isPastClaim && !canOrderNow && claimedItem.addToCalendarLinks && (
            <div>
              <AddToCalendarButton links={claimedItem.addToCalendarLinks} />
            </div>
          )}
        </section>
      )}

      {/* what section */}
      <section className={bem('what')}>
        <div className={bem('section-title')}>{t(translationKeys.what)}</div>

        <div className={bem('claim-description')}>
          {claimedItem.entry.typeName}
          {claimedItem.entry.numberRequired && ` for ${claimedItem.entry.numberRequired}`}
          {isGrubhub && t(translationKeys.deliveryService)}
        </div>

        {isProvideMyself && (
          <>
            {claimedItem.entry.description && (
              <>
                <div className={bem('entry-label')}>{t(translationKeys.description)}</div>
                <div className={bem('entry-text')}>
                  <LinkParser>{claimedItem.entry.description}</LinkParser>
                </div>
              </>
            )}
            {claimedItem.entry.deliveryInstructions && (
              <>
                <div className={bem('entry-label')}>{t(translationKeys.deliveryInstructions)}</div>
                <div className={bem('entry-text')}>
                  <LinkParser>{claimedItem.entry.deliveryInstructions}</LinkParser>
                </div>
              </>
            )}
            {claimedItem.entry.pickupLocation && (
              <>
                <div className={bem('entry-label')}>{t(translationKeys.pickupLocation)}</div>
                <div className={bem('entry-text')}>
                  <LinkParser>{claimedItem.entry.pickupLocation}</LinkParser>
                </div>
              </>
            )}
            {claimedItem.entry.dropoffLocation && (
              <>
                <div className={bem('entry-label')}>{t(translationKeys.dropoffLocation)}</div>
                <div className={bem('entry-text')}>
                  <LinkParser>{claimedItem.entry.dropoffLocation}</LinkParser>
                </div>
              </>
            )}
          </>
        )}

        {!isProvideMyself && (
          <>
            <div className={bem('entry-title')}>{claimedItem.entry.title}</div>
            <p className={bem('entry-description')}>
              {claimedItem.entry.description ? (
                <LinkParser>{claimedItem.entry.description}</LinkParser>
              ) : (
                t(translationKeys.noDescription)
              )}
            </p>
          </>
        )}

        {!isPastClaim && isGrubhub && (
          <div className={bem('grubhub')}>
            {canOrderNow ? (
              <p>{t(translationKeys.toSchedule)}</p>
            ) : (
              <>
                <p>
                  {t(translationKeys.youWillBeAble)}
                  <span>
                    {moment.utc(eventCanOrderFromDate).format('MMMM Do')},{' '}
                    {moment.utc(eventCanOrderFromDate).format('YYYY').toUpperCase()}
                  </span>
                  .
                </p>
                <p>
                  {t(translationKeys.seeWhatsAvailable, {
                    cityAndState:
                      claimedItem.recipientCityAndState.indexOf(', NA') < 0
                        ? `${claimedItem.recipientCityAndState}, so`
                        : '',
                  })}
                </p>
              </>
            )}
            <GrubhubButton url={claimedItem.grubhubUrl} canOrderNow={canOrderNow} />
            {canOrderNow && (
              <p>
                {t(translationKeys.beSureToOrder)}
                <br />
                <span>{getStartDateTime(claimedItem).format('MMMM Do')}</span>
                {!claimedItem.entry.allDay && (
                  <span>
                    , &nbsp;
                    {getStartDateTime(claimedItem).format(DateTimeUtils.Time12HourFormat)}
                    &nbsp;-&nbsp;
                    {getEndDateTime(claimedItem).format(DateTimeUtils.Time12HourFormat)}
                  </span>
                )}
              </p>
            )}
            {canOrderNow && <p className={bem('copy-reminder')}>{t(translationKeys.pleaseRememberToCopy)}</p>}
          </div>
        )}
      </section>

      {/* your notes section */}
      {isProvideMyself && (claimedItem.carType || claimedItem.description || claimedItem.note) && (
        <section className={bem('notes-section')}>
          <div className={bem('notes')}>{t(translationKeys.yourNotes)}</div>

          {claimedItem.carType && (
            <>
              <div className={bem('claim-label')}>{t(translationKeys.carType)}</div>
              <div className={bem('claim-text')}>{<LinkParser>{claimedItem.carType}</LinkParser>}</div>
            </>
          )}
          {claimedItem.description && (
            <>
              <div className={bem('claim-label')}>
                {/* same condition like in Helpers\CalendarLinkGenerator.cs */}
                {claimedItem.entry.typeEventCategory === 'meal'
                  ? t(translationKeys.bringing)
                  : claimedItem.entry.typeId === CalendarEventTypeIds.Other ||
                    claimedItem.entry.typeId === CalendarEventTypeIds.Groceries
                  ? t(translationKeys.providing)
                  : t(translationKeys.contactInformation)}
              </div>
              <div className={bem('claim-text')}>{<LinkParser>{claimedItem.description}</LinkParser>}</div>
            </>
          )}
          {claimedItem.note && (
            <>
              <div className={bem('claim-label')}>{t(translationKeys.personalNote)}</div>
              <div className={bem('claim-text')}>{<LinkParser>{claimedItem.note}</LinkParser>}</div>
            </>
          )}
        </section>
      )}

      {!isProvideMyself && (claimedItem.note || claimedItem.description) && (
        <section className={bem('notes-section')}>
          <div className={bem('notes')}>{t(translationKeys.yourNotes)}</div>

          <div className={bem('claim-description')}>{<LinkParser>{claimedItem.description}</LinkParser>}</div>
          <div className={bem('claim-notes')}>{<LinkParser>{claimedItem.note}</LinkParser>}</div>
        </section>
      )}

      {!isGiftCard && (
        <section className={bem('special-notes-section')}>
          <div className={bem('special-notes-title')}>{t(translationKeys.specialNotes)}</div>

          <p>
            {claimedItem.pageNotes ? (
              <LinkParser>{claimedItem.pageNotes}</LinkParser>
            ) : (
              t(translationKeys.noSpecialNotes)
            )}
          </p>
        </section>
      )}
    </div>
  );
}

const modalBlockName = 'claimed-item-modal';
const bem = bemBlock(modalBlockName);

export const openClaimedItemModal = (props: ClaimedItemModalProps): void => {
  UI.dialog(<ClaimedItemModal {...props} />, {
    className: bem(),
    title: i18n.t(translationKeys.claimInfo),
    closable: true,
    modalProps: { shouldCloseOnOverlayClick: false },
  });
};
